import { createLogger } from '@fabric-app/utils/logging';
import React__default from 'react';

const swipeConfidenceThreshold = 5e3;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};
const targetHasPanningDisabled = (target, panningLayerId, panningAxis) => {
  const targetAttribute = `data-pan-id-ignore-${panningLayerId}`;
  return target.getAttribute(targetAttribute) === panningAxis || target.getAttribute(targetAttribute) === "xy" || !!target.closest(`[${targetAttribute}="${panningAxis}"]`) || !!target.closest(`[${targetAttribute}="xy"]`);
};
const logger = createLogger("usePanning", false);
const isTextSelection = () => "getSelection" in window && !!window.getSelection()?.toString();
const usePanning = (args) => {
  const [panningAxis, setPanningAxis] = React__default.useState(null);
  const [panningOffset, setPanningOffset] = React__default.useState(0);
  const touchOnly = args.touchOnly ?? true;
  const preventEventsDefault = args.preventEventsDefault ?? false;
  const stopEventsPropagation = args.stopEventsPropagation ?? false;
  const { onPanStart: onPanStartProp } = args;
  const onPanStartPropRef = React__default.useRef(onPanStartProp);
  onPanStartPropRef.current = onPanStartProp;
  const { panningLayerId } = args;
  const onPanStart = React__default.useCallback((e, info) => {
    logger.log("panStart", e, info);
    if (isTextSelection()) {
      return;
    }
    if (e.pointerType !== "touch" && touchOnly) {
      return;
    }
    preventEventsDefault && e.preventDefault();
    stopEventsPropagation && e.stopPropagation();
    const panningAxis2 = Math.abs(info.delta.x) > Math.abs(info.delta.y) ? "x" : "y";
    onPanStartPropRef.current?.(e, info, {
      axis: panningAxis2
    });
    if (panningLayerId && e.target && "closest" in e.target && targetHasPanningDisabled(e.target, panningLayerId, panningAxis2)) {
      return;
    }
    if (Math.abs(info.delta.x) > Math.abs(info.delta.y)) {
      setPanningAxis("x");
    } else {
      setPanningAxis("y");
    }
  }, [
    panningLayerId,
    touchOnly,
    preventEventsDefault,
    stopEventsPropagation
  ]);
  const { onPan: onPanProp } = args;
  const onPanPropRef = React__default.useRef(onPanProp);
  onPanPropRef.current = onPanProp;
  const onPan = React__default.useCallback((e, info) => {
    if (isTextSelection()) {
      return;
    }
    if (panningAxis === null) {
      return null;
    }
    preventEventsDefault && e.preventDefault();
    stopEventsPropagation && e.stopPropagation();
    onPanPropRef.current?.(e, info, {
      axis: panningAxis
    });
    if (panningAxis === "y") {
      setPanningOffset(info.offset.y);
    } else {
      setPanningOffset(info.offset.x);
    }
  }, [
    preventEventsDefault,
    panningAxis,
    stopEventsPropagation
  ]);
  const { onPanEnd: onPanEndProp } = args;
  const onPanEndPropRef = React__default.useRef(onPanEndProp);
  onPanEndPropRef.current = onPanEndProp;
  const onPanEnd = React__default.useCallback((e, info) => {
    logger.log("end", e, info);
    if (isTextSelection()) {
      return;
    }
    if (panningAxis === null) {
      return;
    }
    preventEventsDefault && e.preventDefault();
    stopEventsPropagation && e.stopPropagation();
    onPanEndPropRef.current?.(e, info, {
      axis: panningAxis
    });
    setPanningAxis(null);
    setPanningOffset(0);
  }, [
    panningAxis,
    preventEventsDefault,
    stopEventsPropagation
  ]);
  return {
    /**
    * props to mass into a motion component
    */
    motionProps: {
      onPanStart,
      onPan,
      onPanEnd
    },
    /**
    * direction the user is panning
    */
    panningAxis,
    /**
    * offset of the direciton user is panning
    */
    panningOffset,
    isPanningX: panningAxis === "x",
    isPanningY: panningAxis === "y",
    panningXOffset: panningAxis === "x" ? panningOffset : 0,
    panningYOffset: panningAxis === "y" ? panningOffset : 0
  };
};

export { swipeConfidenceThreshold, swipePower, usePanning };
//# sourceMappingURL=usePanning.js.map
